/** angular */
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
/** end angular */

/** services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** end services */

/** interfaces */
import { DsEmitterInterface } from '../interfaces/ds-emitter.interface';
/** end interfaces */

/** models */
import { DsEmitter } from '../models/ds-emitter.class';
/** end models */

/** Sixense */
import { BydModel, BydHttpParams } from '@sixense/byd-core-service';
/** Enums */
import { DsEmitterRightsEnum } from '../enums/ds-emitter-rights.enum';

@Injectable({
  providedIn: 'root',
})
export class DsEmittersService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * Get emitters using pagination. Doesn't take into account user rights, returns all emitters.
   *
   * @param projectId the project id
   * @param elementsPerPage the elements per page
   * @param pageIndex the page index
   * @returns Observable<BydModel<DsEmitter>>
   */
  public getEmitters(projectId: number, elementsPerPage = 10, pageIndex = 0): Observable<BydModel<DsEmitter>> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    this.path = `/v1/projects/${projectId}/emitters`;
    return this.getModels<DsEmitter>({ headers });
  }

  /**
   * Get old emitters list, containing unneeded keys and not implementing pagination.
   * Takes into account user rights.
   *
   * @param projectId the project id
   * @param emitterRightId Emitter right id to filter on.
   * @returns Observable<Emitter[]>
   */
  public getAllEmitters(projectId: number, emitterRightId?: DsEmitterRightsEnum): Observable<DsEmitter[]> {
    this.path = `/projects/${projectId}/emitters`;
    let params = new BydHttpParams();
    if (emitterRightId) {
      params = params.set('roleId', `${emitterRightId}`);
    }
    return this.get({ params });
  }

  /**
   * Add an emitter.
   *
   * @param projectId the project id
   * @param label the emiter label
   * @returns Observable<number>
   */
  public addEmitter(projectId: number, label: string): Observable<number> {
    this.path = `/projects/${projectId}/emitters`;
    return this.post<number>({ body: { label } });
  }

  /**
   * Rename emitter.
   *
   * @param projectId the project id
   * @param campaignId the campaign id
   * @param label the emitter label
   * @returns Observable<boolean>
   */
  public renameEmitter(projectId: number, campaignId: number, label: string): Observable<boolean> {
    this.path = `/projects/${projectId}/emitters/${campaignId}`;
    return this.patch<boolean>({ body: { label } });
  }

  /**
   * Delete emitter.
   *
   * @param projectId the project id
   * @param campaignId the campaign id
   * @returns Observable<boolean>
   */
  public deleteEmitter(projectId: number, campaignId: number): Observable<boolean> {
    this.path = `/projects/${projectId}/emitters/${campaignId}`;
    return this.delete<boolean>();
  }

  /**
   * Create DsEmitter instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `DsEmitter` instance.
   */
  public createNewInstance(jsonObject: DsEmitterInterface): DsEmitter {
    return new DsEmitter(jsonObject);
  }
}
